/* ============================================================
   CARAZON SWIPER — Unified Carousel + Zoom System
   Handles: Main Product Gallery + What's Inside Carousel
   No external dependencies. Pure vanilla JS.
   ============================================================ */

'use strict';

(function () {

    /* ============================================================
       1. CAROUSEL ENGINE — Reusable for any .crz-carousel
       ============================================================ */
    function CrzCarousel(containerEl, options) {
        var self = this;
        var defaults = {
            loop: false,
            autoplay: false,
            autoplayDelay: 5000,
            onChange: null
        };

        self.opts = Object.assign({}, defaults, options || {});
        self.container = containerEl;
        self.track = containerEl.querySelector('.crz-carousel-track');
        self.slides = containerEl.querySelectorAll('.crz-carousel-slide');
        self.totalSlides = self.slides.length;
        self.currentIndex = 0;

        if (self.totalSlides <= 1) {
            // Hide arrows if only 1 slide
            var arrows = containerEl.querySelectorAll('.crz-arrow');
            arrows.forEach(function (a) { a.style.display = 'none'; });
            return;
        }

        self._bindArrows();
        self._bindTouch();
        self._bindDots();
        self._updateState();

        if (self.opts.autoplay) {
            self._startAutoplay();
            containerEl.addEventListener('mouseenter', function () { self._stopAutoplay(); });
            containerEl.addEventListener('mouseleave', function () { self._startAutoplay(); });
        }
    }

    CrzCarousel.prototype.goTo = function (index) {
        if (index < 0) index = this.opts.loop ? this.totalSlides - 1 : 0;
        if (index >= this.totalSlides) index = this.opts.loop ? 0 : this.totalSlides - 1;
        this.currentIndex = index;
        this.track.style.transform = 'translateX(-' + (index * 100) + '%)';
        this._updateState();
        if (typeof this.opts.onChange === 'function') {
            this.opts.onChange(index);
        }
    };

    CrzCarousel.prototype.next = function () { this.goTo(this.currentIndex + 1); };
    CrzCarousel.prototype.prev = function () { this.goTo(this.currentIndex - 1); };

    CrzCarousel.prototype._bindArrows = function () {
        var self = this;
        var prevBtn = self.container.querySelector('.crz-arrow-prev');
        var nextBtn = self.container.querySelector('.crz-arrow-next');
        if (prevBtn) prevBtn.addEventListener('click', function () { self.prev(); });
        if (nextBtn) nextBtn.addEventListener('click', function () { self.next(); });
    };

    CrzCarousel.prototype._bindDots = function () {
        var self = this;
        var dots = self.container.parentElement.querySelectorAll('.crz-dot');
        self.dots = dots;
        dots.forEach(function (dot, i) {
            dot.addEventListener('click', function () { self.goTo(i); });
        });
    };

    CrzCarousel.prototype._bindTouch = function () {
        var self = this;
        var startX = 0;
        var startY = 0;
        var diffX = 0;
        var isDragging = false;
        var isHorizontal = null;

        self.track.addEventListener('touchstart', function (e) {
            startX = e.touches[0].clientX;
            startY = e.touches[0].clientY;
            isDragging = true;
            isHorizontal = null;
            self.container.classList.add('is-dragging');
        }, { passive: true });

        self.track.addEventListener('touchmove', function (e) {
            if (!isDragging) return;
            diffX = e.touches[0].clientX - startX;
            var diffY = e.touches[0].clientY - startY;

            // Determine swipe direction on first significant move
            if (isHorizontal === null && (Math.abs(diffX) > 5 || Math.abs(diffY) > 5)) {
                isHorizontal = Math.abs(diffX) > Math.abs(diffY);
            }

            if (isHorizontal) {
                e.preventDefault();
                var offset = -(self.currentIndex * 100) + (diffX / self.container.offsetWidth * 100);
                self.track.style.transform = 'translateX(' + offset + '%)';
            }
        }, { passive: false });

        self.track.addEventListener('touchend', function () {
            if (!isDragging) return;
            isDragging = false;
            self.container.classList.remove('is-dragging');

            if (isHorizontal) {
                var threshold = self.container.offsetWidth * 0.15;
                if (diffX < -threshold) {
                    self.next();
                } else if (diffX > threshold) {
                    self.prev();
                } else {
                    self.goTo(self.currentIndex);
                }
            }
            diffX = 0;
        }, { passive: true });

        // Mouse drag for desktop
        var mouseStartX = 0;
        var mouseDiffX = 0;
        var mouseDown = false;

        self.track.addEventListener('mousedown', function (e) {
            mouseStartX = e.clientX;
            mouseDown = true;
            self.container.classList.add('is-dragging');
            e.preventDefault();
        });

        document.addEventListener('mousemove', function (e) {
            if (!mouseDown) return;
            mouseDiffX = e.clientX - mouseStartX;
            var offset = -(self.currentIndex * 100) + (mouseDiffX / self.container.offsetWidth * 100);
            self.track.style.transform = 'translateX(' + offset + '%)';
        });

        document.addEventListener('mouseup', function () {
            if (!mouseDown) return;
            mouseDown = false;
            self.container.classList.remove('is-dragging');
            var threshold = self.container.offsetWidth * 0.15;
            if (mouseDiffX < -threshold) {
                self.next();
            } else if (mouseDiffX > threshold) {
                self.prev();
            } else {
                self.goTo(self.currentIndex);
            }
            mouseDiffX = 0;
        });
    };

    CrzCarousel.prototype._updateState = function () {
        var self = this;
        // Update counter
        var counter = self.container.querySelector('.crz-counter');
        if (counter) {
            counter.textContent = (self.currentIndex + 1) + ' / ' + self.totalSlides;
        }
        // Update dots
        if (self.dots && self.dots.length) {
            self.dots.forEach(function (d, i) {
                d.classList.toggle('is-active', i === self.currentIndex);
            });
        }
        // Update arrows visibility (non-loop mode)
        if (!self.opts.loop) {
            var prev = self.container.querySelector('.crz-arrow-prev');
            var next = self.container.querySelector('.crz-arrow-next');
            if (prev) prev.classList.toggle('is-hidden', self.currentIndex === 0);
            if (next) next.classList.toggle('is-hidden', self.currentIndex === self.totalSlides - 1);
        }
    };

    CrzCarousel.prototype._startAutoplay = function () {
        var self = this;
        self._autoInterval = setInterval(function () { self.next(); }, self.opts.autoplayDelay);
    };

    CrzCarousel.prototype._stopAutoplay = function () {
        clearInterval(this._autoInterval);
    };


    /* ============================================================
       2. ZOOM ENGINE — Full-screen image zoom with pan
       ============================================================ */
    var zoomOverlay = null;
    var zoomImg = null;
    var zoomLevel = 1;

    function createZoomOverlay() {
        if (zoomOverlay) return;

        zoomOverlay = document.createElement('div');
        zoomOverlay.className = 'crz-zoom-overlay';
        zoomOverlay.innerHTML =
            '<button class="crz-zoom-close" aria-label="Close zoom" type="button">' +
                '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
            '</button>' +
            '<div class="crz-zoom-container">' +
                '<img src="" alt="" />' +
            '</div>';

        document.body.appendChild(zoomOverlay);
        zoomImg = zoomOverlay.querySelector('.crz-zoom-container img');
        var closeBtn = zoomOverlay.querySelector('.crz-zoom-close');
        var container = zoomOverlay.querySelector('.crz-zoom-container');

        closeBtn.addEventListener('click', closeZoom);

        zoomOverlay.addEventListener('click', function (e) {
            if (e.target === zoomOverlay) closeZoom();
        });

        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape' && zoomOverlay.classList.contains('is-open')) closeZoom();
        });

        // Double-tap / double-click to toggle zoom level
        var lastTap = 0;
        container.addEventListener('click', function (e) {
            var now = Date.now();
            if (now - lastTap < 350) {
                // Double click
                zoomLevel = zoomLevel > 1 ? 1 : 2.5;
                zoomImg.style.transform = 'scale(' + zoomLevel + ')';
            }
            lastTap = now;
        });

        // Scroll wheel zoom
        container.addEventListener('wheel', function (e) {
            e.preventDefault();
            if (e.deltaY < 0) {
                zoomLevel = Math.min(zoomLevel + 0.3, 4);
            } else {
                zoomLevel = Math.max(zoomLevel - 0.3, 1);
            }
            zoomImg.style.transform = 'scale(' + zoomLevel + ')';
        }, { passive: false });

        // Pinch zoom for mobile
        var initialDistance = 0;
        var initialZoom = 1;

        container.addEventListener('touchstart', function (e) {
            if (e.touches.length === 2) {
                initialDistance = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                initialZoom = zoomLevel;
            }
        }, { passive: true });

        container.addEventListener('touchmove', function (e) {
            if (e.touches.length === 2) {
                e.preventDefault();
                var dist = Math.hypot(
                    e.touches[0].clientX - e.touches[1].clientX,
                    e.touches[0].clientY - e.touches[1].clientY
                );
                var scale = initialZoom * (dist / initialDistance);
                zoomLevel = Math.min(Math.max(scale, 1), 4);
                zoomImg.style.transform = 'scale(' + zoomLevel + ')';
            }
        }, { passive: false });
    }

    function openZoom(src, alt) {
        createZoomOverlay();
        zoomLevel = 1;
        zoomImg.style.transform = 'scale(1)';
        zoomImg.src = src;
        zoomImg.alt = alt || '';
        zoomOverlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
    }

    function closeZoom() {
        if (!zoomOverlay) return;
        zoomOverlay.classList.remove('is-open');
        document.body.style.overflow = '';
        zoomLevel = 1;
    }


    /* ============================================================
       3. INIT — Main Product Gallery
       ============================================================ */
    function initProductGallery() {
        var galleryEl = document.querySelector('.pdp-gallery-carousel');
        if (!galleryEl) return;

        var thumbs = document.querySelectorAll('.pdp-thumb');
        var slides = galleryEl.querySelectorAll('.crz-carousel-slide');

        // Init carousel
        var gallery = new CrzCarousel(galleryEl, {
            loop: true,
            onChange: function (index) {
                // Sync thumbnails
                thumbs.forEach(function (t, i) {
                    t.classList.toggle('is-active', i === index);
                });
            }
        });

        // Thumbnail clicks
        thumbs.forEach(function (thumb, i) {
            thumb.addEventListener('click', function () {
                gallery.goTo(i);
            });
        });

        // Zoom — click on main image to open full-screen zoom
        slides.forEach(function (slide) {
            var img = slide.querySelector('img');
            if (!img) return;
            slide.style.cursor = 'zoom-in';
            slide.addEventListener('click', function (e) {
                // Prevent zoom during drag
                if (galleryEl.classList.contains('is-dragging')) return;
                var fullSrc = img.getAttribute('data-zoom-src') || img.src;
                openZoom(fullSrc, img.alt);
            });
        });
    }


    /* ============================================================
       4. INIT — What's Inside Carousel
       ============================================================ */
    function initWiCarousel() {
        var wiCarousels = document.querySelectorAll('.wi-carousel');
        wiCarousels.forEach(function (el) {
            new CrzCarousel(el, { loop: true });
        });
    }


    /* ============================================================
       5. INIT — Letters Section Carousel
       ============================================================ */
    function initLettersCarousel() {
        var lettersCarousels = document.querySelectorAll('.letters-carousel');
        lettersCarousels.forEach(function (el) {
            new CrzCarousel(el, { loop: true });
        });
    }


    /* ============================================================
       6. BOOT — Run on DOM ready
       ============================================================ */
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', boot);
    } else {
        boot();
    }

    function boot() {
        initProductGallery();
        initWiCarousel();
        initLettersCarousel();
    }

})();
